import { createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { RootLayout } from "./shell/RootLayout";
import { ShellLayout } from "./shell/ShellLayout";
import { LoginPage } from "./pages/login/LoginPage";
import { CommandCenterPage } from "./pages/command-center/CommandCenterPage";
import { ProjectsPage } from "./pages/projects/ProjectsPage";
import { ProjectDetailPage } from "./pages/projects/ProjectDetailPage";
import { OnboardingWizardPage } from "./pages/projects/OnboardingWizardPage";
import { TaskDetailPage } from "./pages/tasks/TaskDetailPage";
import { DecisionInboxPage } from "./pages/decisions/DecisionInboxPage";
import { HumanTasksPage } from "./pages/human-tasks/HumanTasksPage";
import { BlockersPage } from "./pages/blockers/BlockersPage";
import { ProposalsPage } from "./pages/proposals/ProposalsPage";
import { PlanPage } from "./pages/plan/PlanPage";
import { GoalsPage } from "./pages/goals/GoalsPage";
import { DepartmentsPage } from "./pages/departments/DepartmentsPage";
import { WorkflowBuilderPage } from "./pages/departments/WorkflowBuilderPage";
import { WorkersPage } from "./pages/workers/WorkersPage";
import { EconomyPage } from "./pages/workers/EconomyPage";
import { WorkerDetailPage } from "./pages/workers/WorkerDetailPage";
import { WorkerComparePage } from "./pages/workers/WorkerComparePage";
import { BrainPage } from "./pages/brain/BrainPage";
import { SessionDetailPage } from "./pages/workers/SessionDetailPage";
import { RoutineDetailPage } from "./pages/workers/RoutineDetailPage";
import { IncidentPage } from "./pages/incidents/IncidentPage";
import { MobileJarvisPage } from "./mobile/MobileJarvisPage";
import { ACTOR_FILTERS, AuditPage } from "./pages/audit/AuditPage";
import { SettingsPage } from "./pages/settings/SettingsPage";
import { OAuthConsentPage } from "./pages/oauth/OAuthConsentPage";
import { NotFoundPage } from "./pages/NotFoundPage";

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

/** Souhlas s MCP konektorem — mimo shell (vlastní obrazovka jako login). */
const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/oauth/consent",
  component: OAuthConsentPage,
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: ShellLayout,
});

const commandRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  // pohled patří do adresy — odkaz na orbitu jde poslat dál
  validateSearch: (search: Record<string, unknown>): { view?: "orbit" } =>
    search.view === "orbit" ? { view: "orbit" } : {},
  component: CommandCenterPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/projects",
  component: ProjectsPage,
});

const projectNewRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/projects/new",
  component: OnboardingWizardPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/projects/$slug",
  component: ProjectDetailPage,
});

const projectTaskRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/projects/$slug/tasks/$number",
  component: TaskDetailPage,
});

const projectDetailTabRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/projects/$slug/$tab",
  // filtr oddělení na kanbanu patří do adresy — pohled „jen QA" jde sdílet
  validateSearch: (search: Record<string, unknown>): { dept?: string } =>
    typeof search.dept === "string" && search.dept ? { dept: search.dept } : {},
  component: ProjectDetailPage,
});

const auditRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/audit",
  // filtr patří do adresy: odkaz na „co dělali workeři" jde poslat dál
  validateSearch: (search: Record<string, unknown>): { actor?: string } => {
    const actor = typeof search.actor === "string" ? search.actor : undefined;
    return actor && ACTOR_FILTERS.includes(actor) ? { actor } : {};
  },
  component: AuditPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/settings",
  component: SettingsPage,
});

const planRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/plan",
  component: PlanPage,
});
const decisionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/decisions",
  // filtr projektu patří do adresy: „koukni na tohle" se posílá odkazem
  validateSearch: (search: Record<string, unknown>): { project?: string } =>
    typeof search.project === "string" && search.project ? { project: search.project } : {},
  component: DecisionInboxPage,
});
/**
 * Čtyři samostatné adresy místo jedné schránky se záložkami. Owner je chce
 * vidět odděleně i s počty: úkol ho zdrží o minuty, blokace stojí celý cyklus
 * workera — a v jednom seznamu to nešlo poznat.
 */
const humanTasksRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/human-tasks",
  validateSearch: (search: Record<string, unknown>): { project?: string } =>
    typeof search.project === "string" && search.project ? { project: search.project } : {},
  component: HumanTasksPage,
});
const blockersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/blockers",
  validateSearch: (search: Record<string, unknown>): { project?: string } =>
    typeof search.project === "string" && search.project ? { project: search.project } : {},
  component: BlockersPage,
});
const proposalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/proposals",
  validateSearch: (search: Record<string, unknown>): { project?: string } =>
    typeof search.project === "string" && search.project ? { project: search.project } : {},
  component: ProposalsPage,
});
const goalsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/goals",
  validateSearch: (search: Record<string, unknown>): { view?: "roadmap" | "trace" } =>
    search.view === "roadmap" || search.view === "trace" ? { view: search.view } : {},
  component: GoalsPage,
});
const economyRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/economy",
  component: EconomyPage,
});

const workersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/workers",
  component: WorkersPage,
});
/**
 * Porovnání musí být zaregistrované vedle `/workers/$id`; statická cesta má
 * v routeru přednost před parametrem, takže se `compare` nikdy nevezme jako id.
 */
const workerCompareRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/workers/compare",
  component: WorkerComparePage,
});
const workerDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/workers/$id",
  component: WorkerDetailPage,
});
const brainRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/brain",
  validateSearch: (search: Record<string, unknown>): { scope?: "FACTORY" | "PROJECT"; project?: string } => {
    const scope = search.scope === "FACTORY" || search.scope === "PROJECT" ? search.scope : undefined;
    const project = typeof search.project === "string" && search.project ? search.project : undefined;
    return { ...(scope ? { scope } : {}), ...(project ? { project } : {}) };
  },
  component: BrainPage,
});
const sessionDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/sessions/$id",
  component: SessionDetailPage,
});
const routineDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/routines/$id",
  component: RoutineDetailPage,
});
const incidentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/incidents/$id",
  component: IncidentPage,
});
const jarvisMobileRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/jarvis",
  component: MobileJarvisPage,
});
const departmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/departments",
  component: DepartmentsPage,
});
const workflowBuilderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/departments/$key",
  component: WorkflowBuilderPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  oauthConsentRoute,
  shellRoute.addChildren([
    commandRoute,
    projectsRoute,
    projectNewRoute,
    projectDetailRoute,
    projectTaskRoute,
    projectDetailTabRoute,
    auditRoute,
    settingsRoute,
    planRoute,
    decisionsRoute,
    humanTasksRoute,
    blockersRoute,
    proposalsRoute,
    goalsRoute,
    workersRoute,
    workerCompareRoute,
    workerDetailRoute,
    economyRoute,
    brainRoute,
    sessionDetailRoute,
    routineDetailRoute,
    incidentRoute,
    jarvisMobileRoute,
    departmentsRoute,
    workflowBuilderRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
